// /src/renderer/layout/TopMenu/TopMenu.tsx
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../shared/components';
import styles from './TopMenu.module.scss';
import SettingModal from '@features/analysis/analysisModal/SettingsModal/SettingsModal';
import PACSModal from '@features/analysis/analysisModal/PACSModal/PACSModal';

import { ReactComponent as User } from 'img/header-user.svg';
import { ReactComponent as Import } from 'img/header-import.svg';
import { ReactComponent as Pacs } from 'img/header-pacs.svg';
import { ReactComponent as Help } from 'img/header-help.svg';
import { ReactComponent as Setting } from 'img/header-setting.svg';
import { ReactComponent as Logout } from 'img/header-logout.svg';
import { ReactComponent as CardiVue } from 'img/autoseg_logo.svg';
import { ReactComponent as Close } from 'img/btn-close.svg';

import { ReactComponent as WindowC } from 'img/w-close.svg';
import { ReactComponent as WindowMin } from 'img/w-min.svg';

import { useModal } from '@components/Modal/ModalProvider';

import RecentStudiesDropdown from './RecentStudiesDropdown';
import { useAuthPage } from '@features/auth/useAuthPage';
import useAuth from '@hooks/Auth/useAuth';
import useFileUploadModal from '@features/analysis/analysisModal/FileUploadModal/useFileUploadModal';
import LogoAndTitle from '@components/Logo/Logo';
import { getVersion } from '@api/version';
import { getLicenseInfo } from '@api/license';
import useModalActions from '@hooks/common/useModalAction';
import { useSettingsModal } from '@features/analysis/analysisModal/SettingsModal/useSettingsModal';
import { useSegmentManager } from '@features/analysis/segmentManager';
import { ICON_MAP } from '@components/Icons/Icons';
import { configStore } from 'config/configStore';

interface TopMenuProps {
  onSettingsClick: () => void;
}

function TopMenu({ onSettingsClick }: TopMenuProps): React.JSX.Element {
  const navigate = useNavigate();
  const { user, isElectron } = useAuthPage();
  const { logout } = useAuth();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { openModal, closeModal } = useModal();
  const { handleOpenFileUploadModal } = useFileUploadModal();
  const { errorDefaultAction } = useModalActions();
  const { general } = useSettingsModal();
  const {mode:manualMode} = useSegmentManager();
  const CloseIcon = ICON_MAP['close'];

  const openPopup = useCallback(async () => {
    await window.electronAPI?.openPopup({
      route: '/popup', // 라우트
      modal: true, // 모달/비모달
      width: 560,
      height: 420,
      data: { userId: 123, mode: 'edit' }, // 전달할 초기 데이터
    });
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout Failed', err);
      navigate('/');
    }
  };

  const settingsModalIdRef = useRef<string | null>(null);
  const pacsModalIdRef = useRef<string | null>(null);

  const handleOpenSettingsModal = useCallback(
    (initialTabId?: string) => {
      const id = openModal(
        <SettingModal
          {...(initialTabId ? { initialTabId } : {})}
          onClose={() => closeModal(id)}
        />,
      );
      settingsModalIdRef.current = id;
    },
    [openModal, closeModal],
  );

  const handleHelpButton = async () => {
    const [status, version_info] = await getVersion();
    const [license_status, license_info] = await getLicenseInfo();

    let msg = `Manufacturer: AI Medic Inc.
      Address: 3F, 45, Seocho-daero 74-gil, Seocho-gu, Seoul, Republic of Korea 06626
      Tel +82.2.568.2667 Fax +82.2.548.2667 E-mail info@aimedic.kr
      \nModel: CardiVue
      UDI-DI: -
      \nThis product is a Software as a Medical Device for professional use.
      The Instructions for Use (e-IFU) are provided electronically via the main menu.
      A paper copy is available free of charge upon request.
      \n`;

    if (status) {
      Object.entries(version_info).forEach((value) => {
        msg += `${value[0]}: ${value[1]}\n`;
      });
      if (license_status) {
      msg += '\n';
      msg += '========================================================\n';
      msg += license_info;
      msg += '\n';
      msg += '========================================================\n';
    }
      let infoModalId = '';
      infoModalId = openModal(
        <div className={styles['info-container']}>
          <div className={styles['info-header']}>
            <div className={styles['info-header-left']}>
              <CardiVue className={styles['info-logo']} />
              <span className={styles['info-title']}>Info</span>
            </div>
            <button
              type="button"
              className={styles['info-close-button']}
              onClick={() => closeModal(infoModalId)}
            >
              <CloseIcon />
            </button>
          </div>

          <span className={styles['info-content']}>
            <span className={styles.text}>{msg}</span>
          </span>
          <div className={styles['info-footer']}>
            <button
              type="button"
              className={styles['info-ok-button']}
              onClick={() => closeModal(infoModalId)}
            >
              <span className={styles.text}>OK</span>
            </button>
          </div>
        </div>,
      );
      // await successAction(msg, undefined, {
      //   modalClassName: 'help-info-modal',
      //   descriptionClassName: 'help-info-description',
      //   headerContent: (
      //     <div className="help-info-header">
      //       <CardiVue className="help-info-logo" />
      //       <span className="help-info-title">Version Info</span>
      //     </div>
      //   ),
      // });
    } else {
      await errorDefaultAction('Failed to get version...');
    }
  };

  const handleOpenPACSModal = useCallback(() => {
    let id: string = 'IMPORT_PACS_MDOAL';
    id = openModal(<PACSModal onClose={() => closeModal(id)} />);
    pacsModalIdRef.current = id;
  }, [openModal, closeModal]);

  useEffect(() => {
    if (!isElectron) return;
    const isFullSize = async () => {
      const isFull = await window.electron.ipcRenderer.isFullScreen();
      setIsFullScreen(isFull);
    };
    window.addEventListener('resize', isFullSize);
    return () => {
      if (isElectron) {
        window.removeEventListener('resize', isFullSize);
      }
    };
  }, [isElectron]);

  // 폰트를 여기서
  useEffect(() => {
    const currentFont = general.font;
    document.body.setAttribute('data-font', currentFont);
  }, [general.font]);

  return (
    <nav className={styles.header}>
      <div className={styles.headerL}>
        {/* <Button onClick={() => navigate('/browser')} className="top-menu-logo">
          <img src={Logo} alt="App Logo" />
        </Button> */}
        <LogoAndTitle />
        {/* <Button.Icon
          icon={<Logo />}
          className="top-logo default-svg-color"
          onClick={() => navigate('/browser')}
        /> */}

        <RecentStudiesDropdown disabled={!!(manualMode && configStore.get().EditMode)}
 />
      </div>
      <div className={styles.headerR}>
        <Button
          startIcon={<User />}
          onClick={() => handleOpenSettingsModal('account')}
        >
          {user?.name
            ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
            : 'No Name'}
          , {user?.email || 'No Email'} (
          {user?.role?.name
            ? user.role.name.charAt(0).toUpperCase() + user.role.name.slice(1)
            : 'No Role'}
          )
        </Button>
        <Button startIcon={<Import />} onClick={handleOpenFileUploadModal}>
          Import
        </Button>
        <Button startIcon={<Pacs />} onClick={() => handleOpenPACSModal()}>
          Import PACS
        </Button>
        {/* {isAdmin && ( */}
        <Button startIcon={<Help />} onClick={handleHelpButton}>
          Info
        </Button>
        {/* )} */}
        <Button
          startIcon={<Setting />}
          onClick={() => handleOpenSettingsModal()}
        >
          Setting
        </Button>
        <Button startIcon={<Logout />} onClick={handleLogout}>
          Logout
        </Button>
        {/* {showMenu && (
          <ThicknessContextMenu onClose={() => setShowMenu(false)} />
        )} */}
        {/* <SettingModal
          open={isSettingModalOpen}
          onClose={() => setIsSettingModalOpen(false)}
        /> */}
        {isElectron && (
          <>
            <WindowBtn
              func={() =>
                window.electron.ipcRenderer.sendMessage('window-handle', [
                  'minimize',
                ])
              }
              image={WindowMin}
            />
            {/* <WindowBtn
              func={() => window.electron.ipcRenderer.sendMessage('window-handle', ['maximize'])}
              image={isFullScreen ? WindowW : WindowMax}
            /> */}
            <WindowBtn
              func={() =>
                window.electron.ipcRenderer.sendMessage('window-handle', [
                  'close',
                ])
              }
              image={WindowC}
            />
          </>
        )}
      </div>
    </nav>
  );
}

export default TopMenu;

function WindowBtn({
  func,
  image: Image,
}: {
  func: () => void;
  image: React.FC<React.SVGProps<SVGSVGElement>>;
}) {
  return (
    <div className={styles.windowBtn} role="presentation" onClick={func}>
      <Image className="windowBtn-icon" />
    </div>
  );
}
